/**
 * Regulatory Radar Page - Intelligence Section
 * Framework update monitoring & horizon scanning with live regulatory intelligence
 */
import React, { useState, useEffect } from 'react';
import {
  Radar,
  Globe,
  AlertTriangle,
  Clock,
  TrendingUp,
  FileText,
  ExternalLink,
  Filter,
  Bell,
  CheckCircle2,
  ArrowUpRight,
  Calendar,
  Tag,
  Info,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IMPACT_COLORS = {
  high: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  medium: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  low: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  info: { bg: 'bg-[#1F1F1F]', text: 'text-[#A0A0A0]', border: 'border-[#2F2F2F]' },
};

const TYPE_ICONS = {
  amendment: FileText,
  new_requirement: AlertTriangle,
  deadline: Clock,
  guidance: Info,
  update: TrendingUp,
};

const FRAMEWORKS = ['All', 'CSRD', 'ESRS', 'CSDDD', 'EU Taxonomy', 'SFDR', 'CBAM', 'TCFD', 'GRI', 'CDP', 'SBTi'];

export default function RegulatoryRadarPage() {
  const [updates, setUpdates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState('All');
  const [selectedImpact, setSelectedImpact] = useState('all');

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { 'Authorization': `Bearer ${getToken()}` };
        const [updatesRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/v1/regulatory-radar/updates`, { headers }).catch(() => null),
          fetch(`${API_URL}/api/v1/regulatory-radar/stats`, { headers }).catch(() => null),
        ]);

        if (updatesRes?.ok) {
          const data = await updatesRes.json();
          setUpdates(data.updates || []);
        } else {
          setUpdates(getDefaultUpdates());
        }

        if (statsRes?.ok) {
          const data = await statsRes.json();
          setStats(data);
        } else {
          setStats(getDefaultStats());
        }
      } catch {
        setUpdates(getDefaultUpdates());
        setStats(getDefaultStats());
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredUpdates = updates.filter(u => {
    if (selectedFramework !== 'All' && u.framework !== selectedFramework) return false;
    if (selectedImpact !== 'all' && u.impact !== selectedImpact) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="regulatory-radar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Radar className="w-6 h-6 text-pink-400" />
            Regulatory Radar
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Monitor regulatory changes and upcoming compliance deadlines
          </p>
        </div>
        <Button variant="outline" className="border-[#2F2F2F]" data-testid="subscribe-alerts-btn">
          <Bell className="w-4 h-4 mr-2" />
          Subscribe to Alerts
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Active Changes" value={stats.active_changes} color="text-amber-400" icon={AlertTriangle} />
          <StatCard label="Upcoming Deadlines" value={stats.upcoming_deadlines} color="text-red-400" icon={Clock} />
          <StatCard label="Frameworks Monitored" value={stats.frameworks_monitored} color="text-blue-400" icon={Globe} />
          <StatCard label="Last 30 Days" value={stats.changes_30d} color="text-emerald-400" icon={TrendingUp} />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <Filter className="w-4 h-4 text-[#6B6B6B]" />
          <span className="text-sm text-[#6B6B6B]">Filter:</span>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FRAMEWORKS.map(fw => (
            <button
              key={fw}
              onClick={() => setSelectedFramework(fw)}
              className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
                selectedFramework === fw
                  ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                  : 'bg-[#1F1F1F] text-[#6B6B6B] border border-transparent hover:border-[#2F2F2F]'
              }`}
            >
              {fw}
            </button>
          ))}
        </div>
        <div className="h-4 w-px bg-[#2F2F2F]" />
        {['all', 'high', 'medium', 'low'].map(impact => (
          <button
            key={impact}
            onClick={() => setSelectedImpact(impact)}
            className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
              selectedImpact === impact
                ? 'bg-white/10 text-white border border-white/20'
                : 'bg-[#1F1F1F] text-[#6B6B6B] border border-transparent hover:border-[#2F2F2F]'
            }`}
          >
            {impact === 'all' ? 'All Impact' : `${impact.charAt(0).toUpperCase() + impact.slice(1)} Impact`}
          </button>
        ))}
      </div>

      {/* Updates List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-pink-400 animate-spin" />
        </div>
      ) : (
        <div className="space-y-3" data-testid="radar-updates-list">
          {filteredUpdates.length === 0 ? (
            <div className="text-center py-12 text-[#6B6B6B]">
              <Radar className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No updates match your filters</p>
            </div>
          ) : (
            filteredUpdates.map((update, i) => (
              <UpdateCard key={i} update={update} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-[#6B6B6B]">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function UpdateCard({ update }) {
  const impact = IMPACT_COLORS[update.impact] || IMPACT_COLORS.info;
  const TypeIcon = TYPE_ICONS[update.type] || FileText;

  return (
    <div className={`p-4 bg-[#111111] border ${impact.border} rounded-xl hover:bg-[#151515] transition-colors`} data-testid={`radar-update-${update.id || ''}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${impact.bg}`}>
            <TypeIcon className={`w-4 h-4 ${impact.text}`} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white">{update.title}</h3>
            <p className="text-xs text-[#6B6B6B] mt-1">{update.description}</p>
          </div>
        </div>
        <Badge className={`${impact.bg} ${impact.text} border-0 shrink-0`}>
          {update.impact}
        </Badge>
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-[#6B6B6B]">
        <span className="flex items-center gap-1">
          <Tag className="w-3 h-3" />
          {update.framework}
        </span>
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {update.date}
        </span>
        {update.effective_date && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Effective: {update.effective_date}
          </span>
        )}
        {update.source_url && (
          <a href={update.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-pink-400 transition-colors">
            <ExternalLink className="w-3 h-3" />
            Source
          </a>
        )}
      </div>
    </div>
  );
}

function getDefaultUpdates() {
  return [
    { id: 1, title: 'ESRS Set 1 Delegated Act - Final Adoption', description: 'The European Commission has adopted the final ESRS Set 1 standards with sector-agnostic requirements for all large EU companies and listed SMEs.', framework: 'ESRS', type: 'new_requirement', impact: 'high', date: '2026-01-15', effective_date: '2026-01-01', source_url: null },
    { id: 2, title: 'CSDDD Implementation Guidance Published', description: 'European Commission released detailed implementation guidance for the Corporate Sustainability Due Diligence Directive, including value chain mapping requirements.', framework: 'CSDDD', type: 'guidance', impact: 'high', date: '2026-02-01', effective_date: '2026-07-01', source_url: null },
    { id: 3, title: 'EU Taxonomy - Technical Screening Criteria Update', description: 'Updated technical screening criteria for climate change mitigation and adaptation objectives, with new thresholds for manufacturing activities.', framework: 'EU Taxonomy', type: 'amendment', impact: 'medium', date: '2026-02-10', effective_date: '2026-06-01', source_url: null },
    { id: 4, title: 'GRI 2024 Amendments - Effective Date Reminder', description: 'GRI Universal Standards 2024 amendments become mandatory for all reporters. Key changes in materiality assessment process and stakeholder engagement requirements.', framework: 'GRI', type: 'deadline', impact: 'medium', date: '2026-02-20', effective_date: '2026-03-01', source_url: null },
    { id: 5, title: 'CDP 2026 Questionnaire Changes', description: 'CDP has announced structural changes to the 2026 Climate Change questionnaire aligning with ISSB S2 and ESRS E1.', framework: 'CDP', type: 'update', impact: 'medium', date: '2026-02-15', source_url: null },
    { id: 6, title: 'CBAM Reporting Phase - Quarterly Deadline', description: 'Quarterly CBAM report submission deadline approaching. Ensure all imported goods data is up to date with embedded emissions calculations.', framework: 'CBAM', type: 'deadline', impact: 'high', date: '2026-02-25', effective_date: '2026-03-31', source_url: null },
    { id: 7, title: 'SBTi Near-Term Target Validation Update', description: 'Updated validation criteria for near-term science-based targets with revised 1.5C pathway requirements.', framework: 'SBTi', type: 'amendment', impact: 'low', date: '2026-01-28', source_url: null },
    { id: 8, title: 'SFDR Principal Adverse Impact Indicators - New Methodology', description: 'Updated calculation methodology for SFDR PAI indicators, affecting fund-level sustainability disclosures.', framework: 'SFDR', type: 'guidance', impact: 'low', date: '2026-02-05', source_url: null },
  ];
}

function getDefaultStats() {
  return { active_changes: 8, upcoming_deadlines: 3, frameworks_monitored: 11, changes_30d: 5 };
}
